import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const NavigationContext = createContext();

export const NavigationProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [history, setHistory] = useState([location.pathname]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Increments only on back/forward — drives forced remount in consumers
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const currentPath = location.pathname;

    if (currentPath === history[currentIndex]) {
      return;
    }

    // Browser back/forward within the tracked stack
    if (currentPath === history[currentIndex - 1]) {
      setCurrentIndex((i) => i - 1);
      setRefreshKey((k) => k + 1);
      return;
    }

    if (currentPath === history[currentIndex + 1]) {
      setCurrentIndex((i) => i + 1);
      setRefreshKey((k) => k + 1);
      return;
    }

    // Regular forward navigation: extend history, drop any forward entries
    setHistory((prev) => {
      const trimmed = prev.slice(0, currentIndex + 1);
      return [...trimmed, currentPath];
    });
    setCurrentIndex((i) => i + 1);
  }, [location.pathname, location.key]);

  const canGoBack = () => currentIndex > 0;
  const canGoForward = () => currentIndex < history.length - 1;

  const goBack = () => {
    if (!canGoBack()) return;
    navigate(-1);
  };

  const goForward = () => {
    if (!canGoForward()) return;
    navigate(1);
  };

  return (
    <NavigationContext.Provider
      value={{
        goBack,
        goForward,
        canGoBack: canGoBack(),
        canGoForward: canGoForward(),
        history,
        currentIndex,
        currentPath: location.pathname,
        // Combine path + refreshKey so the key changes on every back/forward
        pageKey: `${location.pathname}-${refreshKey}`,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
};